import { useEffect, useState } from "react";
import {
  PhasePill,
  Timer,
  LiveDot,
  LiveBar,
  Eyebrow,
  Heading,
  Hairline,
  EventFeed,
  ScoreBreakdown,
  type BeaconPhase,
} from "../components/primitives";

/**
 * /dev/primitives — isolierte Test-Route, zeigt jede Beacon-Primitive
 * in beiden Skins (presenter / light) nebeneinander.
 *
 * Aufruf: http://localhost:5173/dev/primitives
 */

const PHASES: BeaconPhase[] = [
  "waiting",
  "topics",
  "voting",
  "winner-reveal",
  "grouping",
  "working",
  "submitted",
  "evaluating",
  "evaluated",
  "showcase",
  "overnight",
  "reveal",
  "done",
];

const SAMPLE_EVENTS = [
  { ts: Date.now() - 12000, tag: "boot", message: "Eval-Lambda gestartet" },
  { ts: Date.now() - 9000, tag: "team-01", message: "Bewertung läuft …" },
  { ts: Date.now() - 6000, tag: "team-01", message: "Score 8.2 / 10", ok: true },
  { ts: Date.now() - 3000, tag: "team-02", message: "Bewertung läuft …" },
  { ts: Date.now() - 1500, tag: "team-02", message: "Score 7.4 / 10", ok: true },
];

const SCORE_CRITERIA = [
  { label: "INNOVATION", value: 8.4 },
  { label: "WIRTSCHAFTLICH", value: 7.1 },
  { label: "STRATEGIE", value: 8.8 },
  { label: "UMSETZUNG", value: 6.5 },
];

export function PrimitivesGallery() {
  // 5-Minuten Timer
  const [deadline] = useState(() => Date.now() + 5 * 60 * 1000);
  // 9-Sekunden Crit-Timer
  const [critDeadline, setCritDeadline] = useState(() => Date.now() + 9 * 1000);

  useEffect(() => {
    const id = window.setInterval(() => {
      // Crit-Timer reset bei Ablauf für Demo
      if (critDeadline <= Date.now()) {
        setCritDeadline(Date.now() + 9 * 1000);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [critDeadline]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        minHeight: "100vh",
      }}
    >
      <Skin variant="presenter" deadline={deadline} critDeadline={critDeadline} />
      <Skin variant="light" deadline={deadline} critDeadline={critDeadline} />
    </div>
  );

  function Skin({
    variant,
    deadline,
    critDeadline,
  }: {
    variant: "presenter" | "light";
    deadline: number;
    critDeadline: number;
  }) {
    const isPresenter = variant === "presenter";
    const bg = isPresenter ? "var(--ink-800)" : "var(--paper-50)";
    const fg = isPresenter ? "var(--svo-weiss)" : "var(--paper-900)";
    return (
      <section
        className={isPresenter ? "skin-presenter-scope" : "skin-light-scope"}
        style={{
          background: bg,
          color: fg,
          padding: "var(--s-7)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-7)",
          borderRight: isPresenter ? "1px solid rgba(255,255,255,0.08)" : "none",
        }}
      >
        <header style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Eyebrow accent>
            {isPresenter ? "PRESENTER · DARK" : "LIGHT · MOBILE / SHOWCASE / ADMIN"}
          </Eyebrow>
          <Heading level="h2">Beacon Primitives</Heading>
        </header>

        <Block label="PHASE-PILLS">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-3)" }}>
            {PHASES.map((p) => (
              <PhasePill key={p} phase={p} />
            ))}
          </div>
        </Block>

        <Block label="TIMER">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--s-6)", flexWrap: "wrap" }}>
            <Timer deadline={deadline} size="inline" />
            <Timer deadline={deadline} size="side" />
            <Timer deadline={critDeadline} size="inline" critAtSeconds={10} warnAtSeconds={60} />
          </div>
          <div style={{ marginTop: "var(--s-5)" }}>
            <Timer deadline={deadline} size="hero" />
          </div>
        </Block>

        <Block label="LIVE-DOT">
          <div style={{ display: "flex", alignItems: "center", gap: "var(--s-5)" }}>
            <Pair label="static"><LiveDot /></Pair>
            <Pair label="live"><LiveDot live /></Pair>
            <Pair label="ok"><LiveDot ok /></Pair>
          </div>
        </Block>

        <Block label="LIVE-BAR">
          <div style={{ display: "grid", gap: "var(--s-3)" }}>
            <LiveBar value={3} max={10} skin={variant} />
            <LiveBar value={6} max={10} skin={variant} />
            <LiveBar value={9} max={10} skin={variant} winner />
          </div>
        </Block>

        <Block label="HEADINGS">
          <Heading level="display">Woran wollen wir heute arbeiten<em style={{ fontStyle: "normal", color: "var(--accent-blue-soft)" }}>?</em></Heading>
          <Heading level="h1">Top 3.</Heading>
          <Heading level="h2">Bewertung läuft.</Heading>
          <Heading level="h3">90 Minuten.</Heading>
        </Block>

        <Block label="HAIRLINE">
          <div style={{ display: "grid", gap: "var(--s-3)" }}>
            <Hairline skin={variant} />
            <Hairline skin={variant} />
            <Hairline skin={variant} />
          </div>
        </Block>

        <Block label="EVENT-FEED">
          <EventFeed events={SAMPLE_EVENTS} cursor skin={variant} />
        </Block>

        <Block label="SCORE-BREAKDOWN">
          <ScoreBreakdown criteria={SCORE_CRITERIA} skin={variant} />
        </Block>
      </section>
    );

    function Block({ label, children }: { label: string; children: React.ReactNode }) {
      return (
        <section style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
          <Eyebrow>{label}</Eyebrow>
          <Hairline skin={variant} />
          <div style={{ marginTop: "var(--s-2)" }}>{children}</div>
        </section>
      );
    }

    function Pair({ label, children }: { label: string; children: React.ReactNode }) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
          {children}
          <Eyebrow>{label}</Eyebrow>
        </div>
      );
    }
  }
}
